import React, {useMemo} from 'react'
import {Text} from '@chakra-ui/react'
import useMyStokkeSubroutes from '../hooks/use-subroutes'
import {
    ACCOUNT_HREF,
    MY_STOKKE_ADDRESSES_PAGE_ID,
    MY_STOKKE_DASHBOARD_PAGE_ID,
    MY_STOKKE_ORDERS_PAGE_ID,
    MY_STOKKE_WARRANTY_PAGE_ID,
    MY_STOKKE_WISHLIST_PAGE_ID
} from '../../../constants'
import {useIntl} from 'react-intl'
import useOcapiSite from '../../../hooks/useOcapiSite'
import {ArrowRightIcon} from '../../../leap/components/icons'
import PropTypes from 'prop-types'
import Link from '../../../leap/components/link'

/**
 * The items by config ids in the order they should appear in the dropdown. Missing items in config will not be displayed.
 */
const ITEM_CONFIG_IDS = [
    MY_STOKKE_DASHBOARD_PAGE_ID,
    MY_STOKKE_ORDERS_PAGE_ID,
    MY_STOKKE_WARRANTY_PAGE_ID,
    MY_STOKKE_ADDRESSES_PAGE_ID,
    MY_STOKKE_WISHLIST_PAGE_ID
]

/**
 * List of menu items that represent the my stokke navigation items displayed on click of my account button in header
 */
const MyStokkeNavigationDropdown = ({menuItemComponent: MenuItemComponent}) => {
    const {formatMessage} = useIntl()
    const subroutes = useMyStokkeSubroutes()
    const {wishlistEnabled} = useOcapiSite().preferences

    // map ITEM_CONFIG_IDS to subroutes
    const items = useMemo(() => {
        const itemsByConfigId = Object.fromEntries(
            subroutes.map((route) => [route.configId, route])
        )

        return ITEM_CONFIG_IDS.map((configId) => itemsByConfigId[configId]).filter(
            // leave items defined in myStokkeConfig and dashboard (has label)
            (item) => {
                if (item.config?.id === 'wishlist') {
                    return wishlistEnabled && (item?.config || item?.label)
                } else {
                    return item?.config || item?.label
                }
            }
        )
    }, [subroutes, wishlistEnabled])

    return (
        <>
            {items.map((route, idx) => {
                const Icon = route.icon
                const isBroken =
                    // without headline or label there's no text to display
                    !route.config?.headline && !route.label
                return (
                    <MenuItemComponent
                        key={idx}
                        as={Link}
                        href={route.config?.link}
                        to={route.path && `${ACCOUNT_HREF}${route.path}`}
                        color={isBroken ? 'stokkeFunctional.alertRed' : null}
                        icon={<Icon boxSize={4} mt={0.75} />}
                        endElement={<ArrowRightIcon boxSize={4} />}
                        showEndElementOnHover
                        justifyContent="space-between"
                        marginInline="0"
                        _focusVisible={{
                            paddingInlineStart: 'spacing-12',
                            paddingInlineEnd: 'spacing-16'
                        }}
                    >
                        <Text as="span" minW={0} flex="1" _after={{display: 'none'}}>
                            {route.config?.headline
                                ? route.config.headline
                                : route.label
                                ? formatMessage(route.label)
                                : `Missing "${route.configId}"`}
                        </Text>
                    </MenuItemComponent>
                )
            })}
        </>
    )
}

MyStokkeNavigationDropdown.propTypes = {
    menuItemComponent: PropTypes.elementType.isRequired
}

export default MyStokkeNavigationDropdown
